import { Permission, type PermissionValue, type Role } from './types';

const BASIC_PROJECT_PERMISSIONS = [
    Permission.VIEW_PROJECT,
    Permission.EDIT_OWN_PROFILE,
    Permission.UPDATE_OWN_PROFILE,
    Permission.MANAGE_FEED,
    Permission.CREATE_FILES,
];

const PERSONNEL_ACCESS_PERMISSIONS = [
    Permission.EDIT_PERSONNEL_OPERATIONAL,
    Permission.EDIT_CASTING_ASSIGNMENTS,
    Permission.MANAGE_MEMBERS,
];

const ACTION_ITEMS_EDIT_PERMISSIONS = [
    Permission.VIEW_ACTION_ITEMS,
    Permission.EDIT_ACTION_ITEMS,
];

const STAGE_MANAGEMENT_PERMISSIONS = [
    Permission.VIEW_ALL,
    Permission.EDIT_RUNSHEET,
    Permission.EDIT_PROPS,
    Permission.EDIT_COSTUMES,
    Permission.EDIT_SETS,
    Permission.EDIT_BLOCKING,
    Permission.EDIT_SCHEDULER,
    Permission.EDIT_CAST,
    Permission.EDIT_NOTES,
    ...ACTION_ITEMS_EDIT_PERMISSIONS,
    Permission.EDIT_SOUND,
    Permission.EDIT_LIGHTING,
    Permission.EDIT_PROJECTIONS,
    Permission.VIEW_PROMPTBOOK,
    Permission.EDIT_PROMPTBOOK,
    Permission.EDIT_FILES,
    Permission.MANAGE_FILE_ACCESS,
    Permission.MANAGE_USERS,
    Permission.VIEW_BUDGET,
    Permission.VIEW_PRODUCTION,
    Permission.CALL_SHOW,
];

const PRODUCTION_MANAGEMENT_PERMISSIONS = [
    Permission.VIEW_ALL,
    Permission.EDIT_RUNSHEET,
    Permission.EDIT_PROPS,
    Permission.EDIT_COSTUMES,
    Permission.EDIT_SETS,
    Permission.EDIT_BLOCKING,
    Permission.EDIT_SCHEDULER,
    Permission.EDIT_CAST,
    Permission.EDIT_NOTES,
    ...ACTION_ITEMS_EDIT_PERMISSIONS,
    Permission.EDIT_SOUND,
    Permission.EDIT_LIGHTING,
    Permission.EDIT_PROJECTIONS,
    Permission.EDIT_FILES,
    Permission.MANAGE_FILE_ACCESS,
    Permission.MANAGE_USERS,
    Permission.VIEW_BUDGET,
    Permission.EDIT_BUDGET,
    Permission.VIEW_PRODUCTION,
    Permission.EDIT_PRODUCTION,
];

const COMMUNICATIONS_PERMISSIONS = [
    Permission.SEND_EMAIL,
    Permission.MANAGE_COMMUNICATIONS,
];

function uniquePermissions(permissions: PermissionValue[]): PermissionValue[] {
    return [...new Set(permissions)];
}

function shouldMergeDefaultRolePermissions(role: Role): boolean {
    return role.isBuiltIn !== false
        && role.customized !== true
        && typeof role.sourceTemplateId !== 'string';
}

/**
 * Default role definitions
 * These are copied to each project on creation
 */
export const DEFAULT_ROLES: Role[] = [
    {
        id: 'owner',
        name: 'Owner',
        permissions: uniquePermissions([
            ...BASIC_PROJECT_PERMISSIONS,
            Permission.MANAGE_PROJECT_OWNER,
            Permission.MANAGE_PROJECT_ADMINS,
            ...PERSONNEL_ACCESS_PERMISSIONS,
            ...COMMUNICATIONS_PERMISSIONS,
            ...STAGE_MANAGEMENT_PERMISSIONS,
            ...PRODUCTION_MANAGEMENT_PERMISSIONS,
            Permission.MANAGE_PROJECT,
            Permission.DELETE_PROJECT,
        ]),
        isBuiltIn: true,
        description: 'Full project ownership, access management, and operational access'
    },
    {
        id: 'stage-manager',
        name: 'Stage Manager',
        permissions: [
            ...BASIC_PROJECT_PERMISSIONS,
            ...PERSONNEL_ACCESS_PERMISSIONS,
            Permission.MANAGE_PROJECT_ADMINS,
            ...COMMUNICATIONS_PERMISSIONS,
            ...STAGE_MANAGEMENT_PERMISSIONS,
        ],
        isBuiltIn: true,
        description: 'Full stage-management access and project-admin assignment'
    },
    {
        id: 'production-manager',
        name: 'Production Manager',
        permissions: [
            ...BASIC_PROJECT_PERMISSIONS,
            ...PERSONNEL_ACCESS_PERMISSIONS,
            Permission.MANAGE_PROJECT_ADMINS,
            ...COMMUNICATIONS_PERMISSIONS,
            ...PRODUCTION_MANAGEMENT_PERMISSIONS,
        ],
        isBuiltIn: true,
        description: 'Production access, budgets, and project-admin assignment'
    },
    {
        id: 'assistant-stage-manager',
        name: 'Assistant Stage Manager',
        permissions: [
            ...BASIC_PROJECT_PERMISSIONS,
            ...PERSONNEL_ACCESS_PERMISSIONS,
            Permission.SEND_EMAIL,
            ...STAGE_MANAGEMENT_PERMISSIONS,
        ],
        isBuiltIn: true,
        description: 'Stage-management access and non-admin member management'
    },
    {
        id: 'director',
        name: 'Director',
        permissions: [
            ...BASIC_PROJECT_PERMISSIONS,
            Permission.EDIT_CASTING_ASSIGNMENTS,
            Permission.VIEW_ALL,
            Permission.EDIT_SCHEDULER,
            Permission.EDIT_BLOCKING,
            Permission.EDIT_NOTES,
            ...ACTION_ITEMS_EDIT_PERMISSIONS,
            Permission.VIEW_PROMPTBOOK,
        ],
        isBuiltIn: true,
        description: 'Creative access and casting assignment management'
    },
    {
        id: 'designer',
        name: 'Designer',
        permissions: [
            ...BASIC_PROJECT_PERMISSIONS,
            Permission.VIEW_ALL,
            Permission.EDIT_PROPS,
            Permission.EDIT_COSTUMES,
            Permission.EDIT_SETS,
            Permission.EDIT_SOUND,
            Permission.EDIT_LIGHTING,
            Permission.EDIT_PROJECTIONS,
            Permission.EDIT_FILES,
            Permission.MANAGE_FILE_ACCESS,
            Permission.VIEW_BUDGET,
            Permission.VIEW_PRODUCTION,
        ],
        isBuiltIn: true,
        description: 'Edit props, sets, and files'
    },
    {
        id: 'cast',
        name: 'Cast',
        permissions: [
            ...BASIC_PROJECT_PERMISSIONS,
            Permission.SELF_CHECK_IN,
        ],
        isBuiltIn: true,
        description: 'View project, check-in, update own profile'
    },
    {
        id: 'crew',
        name: 'Crew',
        permissions: [...BASIC_PROJECT_PERMISSIONS],
        isBuiltIn: true,
        description: 'Basic project and profile access'
    },
    {
        id: 'basic-member',
        name: 'Basic Member',
        permissions: [...BASIC_PROJECT_PERMISSIONS],
        isBuiltIn: true,
        description: 'Basic project and profile access'
    },
];

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: Role | null | undefined, permission: PermissionValue): boolean {
    if (!role) return false;
    const permissions = Array.isArray(role.permissions) ? role.permissions : [];
    return permissions.includes(permission);
}

/**
 * Get role by ID from a list
 */
export function getRoleById(roles: Role[], roleId: string): Role | undefined {
    return roles.find(r => r.id === roleId);
}

export function mergeRolesWithDefaultPermissions(roles: Role[]): Role[] {
    const defaultsById = new Map(DEFAULT_ROLES.map(role => [role.id, role]));
    const mergedRoleIds = new Set<string>();

    const merged = roles.map(role => {
        mergedRoleIds.add(role.id);
        const defaultRole = defaultsById.get(role.id);
        if (!defaultRole || !shouldMergeDefaultRolePermissions(role)) {
            return role;
        }

        return {
            ...defaultRole,
            ...role,
            permissions: uniquePermissions([
                ...defaultRole.permissions,
                ...(Array.isArray(role.permissions) ? role.permissions : []),
            ]),
            isBuiltIn: role.isBuiltIn ?? defaultRole.isBuiltIn,
            description: role.description ?? defaultRole.description,
        };
    });

    for (const defaultRole of DEFAULT_ROLES) {
        if (!mergedRoleIds.has(defaultRole.id)) {
            merged.push(defaultRole);
        }
    }

    return merged;
}











